import { AnalysisResult, ColumnType } from './useAnalyseCSV';

export interface ColumnMapping {
    // fieldId → column indices assigned to it.
    // "property:<key>" prefix denotes a GeneralSettings.clientProperties entry.
    // notes, internalNotes, and phoneNumbers accept multiple indices.
    fieldMap: Record<string, number[]>;
    unmappedCols: number[];
}

export interface CustomProperty {
    key: string;
    description?: string;
}

// Fields that combine all assigned columns. All other fields accept multiple columns too,
// but use the first non-empty value (fallback order matches the chip order in the UI).
export const MULTI_COLUMN_FIELDS = new Set(['notes', 'internalNotes', 'phoneNumbers']);

// All standard importable client fields, in display order
export const STANDARD_FIELDS = [
    'name',
    'taxNumber',
    'healthNumber',
    'identificationNumber',
    'clientFileNumber',
    'phoneNumbers',
    'email',
    'sex',
    'birthday',
    'birthdayYear',
    'birthdayMonth',
    'birthdayDay',
    'job',
    'origin',
    'subsystemName',
    'subsystemNumber',
    'addressLine1',
    'addressLine2',
    'zipCode',
    'city',
    'tags',
    'notes',
    'internalNotes',
    'warningNote',
    'status',
    'rgpdApproved',
    'acceptMarktingCommunication',
];

// Alias table: fieldId → normalised column name variants
const FIELD_ALIASES: Record<string, string[]> = {
    name: ['nome', 'cliente', 'full_name', 'name', 'client', 'paciente', 'patient'],
    taxNumber: ['nif', 'contribuinte', 'vat_number', 'tax_number', 'taxnumber', 'numero fiscal'],
    healthNumber: ['cartao saude', 'healthcard', 'health_number', 'healthnumber', 'no saude', 'num. utente', 'numero utente', 'utente'],
    identificationNumber: ['identificacao', 'identificationnumber', 'identification_number', 'bi', 'cc', 'numero identificacao', 'num. identificacao'],
    subsystemName: ['subsistema', 'subsystemname', 'subsystem_name'],
    subsystemNumber: ['no beneficiario', 'beneficiarynumber', 'beneficiary_number', 'subsystem_number', 'num. beneficiario'],
    clientFileNumber: ['ficha', 'nr', 'codigo', 'filenumber', 'file_number', 'numero ficha', 'n. ficha', 'num. ficha'],
    phoneNumbers: ['contacto', 'telefones', 'telemovel', 'mobile', 'phone_main_note', 'telefone', 'phone', 'tel', 'telef', 'phone number', 'numero telefone'],
    email: ['email', 'e-mail', 'e_mail', 'correio eletronico', 'mail'],
    sex: ['sexo', 'gender', 'sex', 'genero'],
    birthday: ['data nasc.', 'aniversario', 'data nascimento', 'nascimento', 'birthday', 'date of birth', 'dob', 'data nasc', 'data de nascimento'],
    birthdayYear: ['birth_year', 'ano nascimento', 'ano_nascimento', 'year of birth', 'ano nasc', 'birth year', 'year'],
    birthdayMonth: ['birth_month', 'mes nascimento', 'mes_nascimento', 'month of birth', 'mes nasc', 'birth month', 'month'],
    birthdayDay: ['birth_day', 'dia nascimento', 'dia_nascimento', 'day of birth', 'dia nasc', 'birth day', 'day'],
    job: ['profissao', 'job', 'ocupacao', 'occupation', 'cargo', 'jobindex'],
    origin: ['origem', 'crm_source', 'origin', 'como conheceu', 'fonte', 'partnershipindex'],
    addressLine1: ['morada', 'endereco', 'address', 'addressline1', 'address_line1', 'rua', 'street', 'linha morada'],
    addressLine2: ['localidade', 'addressline2', 'address_line2', 'bairro', 'freguesia'],
    zipCode: ['codigo postal', 'cod. postal', 'zipcode', 'zip_code', 'zip', 'postal_code', 'cp', 'codigo-postal'],
    city: ['cidade', 'city'],
    notes: ['observacoes', 'obs_1', 'obs_2', 'obs_3', 'online', 'invoice_obs', 'notas', 'notes', 'observacao', 'obs', 'anotacoes', 'comentarios'],
    internalNotes: ['notas internas', 'internal_notes', 'internalnotes', 'notas_internas'],
    warningNote: ['aviso', 'warning', 'warning_note', 'nota aviso', 'alerta'],
    tags: ['segment_manual', 'tags', 'etiquetas', 'categorias', 'labels', 'grupos'],
    rgpdApproved: ['rgpd_updated_on', 'rgpd', 'gdpr', 'consentimento', 'rgpd_aprovado'],
    acceptMarktingCommunication: ['marketing', 'accept_marketing', 'aceita_marketing', 'comunicacao_marketing'],
    status: ['status', 'estado', 'situacao'],
};

// Type-based fallback: if alias matching fails, use column type as a hint
const TYPE_FALLBACKS: Partial<Record<ColumnType, string>> = {
    phone: 'phoneNumbers',
    email: 'email',
    sex: 'sex',
};

export function normalizeHeader(header: string): string {
    return header
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .replace(/\s+/g, ' ');
}

function findFieldForHeader(
    normalizedHeader: string,
    alreadyMapped: Set<string>,
): string | null {
    for (const [fieldId, aliases] of Object.entries(FIELD_ALIASES)) {
        if (!MULTI_COLUMN_FIELDS.has(fieldId) && alreadyMapped.has(fieldId)) continue;
        if (aliases.includes(normalizedHeader)) return fieldId;
    }
    return null;
}

export function autoMap(result: AnalysisResult, customProperties: CustomProperty[]): ColumnMapping {
    const fieldMap: Record<string, number[]> = {};
    const assignedCols = new Set<number>();
    const alreadyMapped = new Set<string>();

    const assign = (fieldId: string, colIndex: number) => {
        if (!fieldMap[fieldId]) fieldMap[fieldId] = [];
        fieldMap[fieldId].push(colIndex);
        assignedCols.add(colIndex);
        if (!MULTI_COLUMN_FIELDS.has(fieldId)) alreadyMapped.add(fieldId);
    };

    // Pass 1: alias matching
    result.headers.forEach((header, i) => {
        const fieldId = findFieldForHeader(normalizeHeader(header), alreadyMapped);
        if (fieldId) assign(fieldId, i);
    });

    // Pass 2: type-based fallback for columns not yet assigned
    result.headers.forEach((_, i) => {
        if (assignedCols.has(i)) return;
        const colType = result.columnTypes[i];
        const fallbackField = colType ? TYPE_FALLBACKS[colType] : undefined;
        if (fallbackField && !alreadyMapped.has(fallbackField)) assign(fallbackField, i);
    });

    // Pass 3: date fallback → birthday if not already mapped
    if (!alreadyMapped.has('birthday')) {
        const firstDateCol = result.columnTypes.findIndex((t, i) => t === 'date' && !assignedCols.has(i));
        if (firstDateCol !== -1) assign('birthday', firstDateCol);
    }

    // Pass 4: custom property matching — by description first, then by key
    result.headers.forEach((header, i) => {
        if (assignedCols.has(i)) return;
        const normalized = normalizeHeader(header);
        const match = customProperties.find(p =>
            (p.description && normalizeHeader(p.description) === normalized) ||
            normalizeHeader(p.key) === normalized
        );
        if (match) assign(`property:${match.key}`, i);
    });

    const unmappedCols = result.headers.map((_, i) => i).filter(i => !assignedCols.has(i));

    return { fieldMap, unmappedCols };
}
